/**
 * PromotedListingsSection — renders promoted-retailer search hits (#149, §23).
 *
 * A "promoted" retailer is one an admin has flagged so its matching in-stock
 * listings are pinned to the top of search, clearly labeled "Promoted". This
 * is distinct from paid "Sponsored" placements (see SponsoredListingsSection):
 *
 *   - Promotion is a retailer-level editorial boost, NOT behavioral targeting,
 *     so — unlike sponsored — it is NOT hidden when the user opts out of
 *     behavioral advertising (§10.7 separates these consent surfaces).
 *   - The same §23.3 suppression rules still apply (stale / out-of-stock); the
 *     backend already enforces trust / caliber relevance. The frontend mirrors
 *     the visible suppression here as a defense-in-depth.
 *
 * Labeling (§28.4, §16 WCAG 2.2 AA):
 *   - Visible "Promoted" heading (role heading).
 *   - <section> is aria-labelledby that heading so AT announces "Promoted,
 *     region" when the user navigates into it.
 *   - Each card carries an aria-label="Promoted" chip (ListingCard), so a
 *     promoted hit is programmatically distinguishable from organic.
 *   - Empty slot is kept in the DOM as an empty labeled <section> so
 *     screen-reader virtual-cursor navigation is stable.
 *
 * @module PromotedListingsSection
 */

import { Box, Typography } from '@mui/material';

import { ListingCard } from './ListingCard';
import type { SearchHit } from '../../services/searchApi';

/** Maximum number of promoted hits to display in one page (mirrors backend). */
export const MAX_PROMOTED = 3;

export interface PromotedListingsSectionProps {
  /** All hits returned by the search API (sponsored + promoted + organic). */
  hits: SearchHit[];
}

/**
 * Filter and cap the raw hit list down to eligible promoted entries.
 *
 * Suppression order:
 *   1. Keep only hits where `isPromoted === true` and NOT also sponsored
 *      (a paid sponsored slot is rendered by SponsoredListingsSection).
 *   2. Drop stale listings (dataFreshness === 'stale' | 'unavailable').
 *   3. Drop out-of-stock listings (stockStatus === 'out_of_stock').
 *   4. Enforce the MAX_PROMOTED display cap.
 */
function eligiblePromoted(hits: SearchHit[]): SearchHit[] {
  return hits
    .filter((h) => h.isPromoted && !h.isSponsored)
    .filter((h) => h.dataFreshness !== 'stale' && h.dataFreshness !== 'unavailable')
    .filter((h) => h.stockStatus !== 'out_of_stock')
    .slice(0, MAX_PROMOTED);
}

/**
 * Promoted listings section for the search results page.
 *
 * Usage:
 * ```tsx
 * <PromotedListingsSection hits={searchQuery.data?.results ?? []} />
 * ```
 */
export function PromotedListingsSection({ hits }: PromotedListingsSectionProps) {
  const visible = eligiblePromoted(hits);

  if (visible.length === 0) {
    return (
      <Box
        component="section"
        aria-label="Promoted listings"
        aria-live="polite"
        data-testid="promoted-slot"
        sx={{ minHeight: 0 }}
      />
    );
  }

  return (
    <Box
      component="section"
      aria-labelledby="promoted-heading"
      aria-live="polite"
      data-testid="promoted-slot"
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 2,
        bgcolor: 'background.paper',
      }}
    >
      <Typography
        id="promoted-heading"
        variant="overline"
        component="h2"
        sx={{ display: 'block', mb: 1, color: 'text.secondary' }}
      >
        Promoted
      </Typography>
      <Box
        component="ul"
        sx={{
          listStyle: 'none',
          p: 0,
          m: 0,
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
          },
          gap: 2,
        }}
      >
        {visible.map((hit) => (
          <Box component="li" key={hit.listingId} sx={{ display: 'flex' }}>
            <Box sx={{ width: '100%' }}>
              <ListingCard hit={hit} variant="full" />
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default PromotedListingsSection;
